/**
 * Local storage utilities for persistent data.
 */

import type { AddressResult } from '../types'

const STORAGE_KEYS = {
  HOME_ADDRESS: 'prime_taxi_home_address',
  WORK_ADDRESS: 'prime_taxi_work_address',
  RECENT_ADDRESSES: 'prime_taxi_recent_addresses',
  USER_PREFERENCES: 'prime_taxi_preferences',
} as const

// Home/Work addresses
export interface SavedAddresses {
  home: AddressResult | null
  work: AddressResult | null
}

export const getSavedAddresses = (): SavedAddresses => {
  try {
    const home = localStorage.getItem(STORAGE_KEYS.HOME_ADDRESS)
    const work = localStorage.getItem(STORAGE_KEYS.WORK_ADDRESS)
    return {
      home: home ? JSON.parse(home) : null,
      work: work ? JSON.parse(work) : null,
    }
  } catch (error) {
    console.error('Error reading saved addresses:', error)
    return { home: null, work: null }
  }
}

export const saveHomeAddress = (address: AddressResult | null): void => {
  try {
    if (address) {
      localStorage.setItem(STORAGE_KEYS.HOME_ADDRESS, JSON.stringify(address))
    } else {
      localStorage.removeItem(STORAGE_KEYS.HOME_ADDRESS)
    }
  } catch (error) {
    console.error('Error saving home address:', error)
  }
}

export const saveWorkAddress = (address: AddressResult | null): void => {
  try {
    if (address) {
      localStorage.setItem(STORAGE_KEYS.WORK_ADDRESS, JSON.stringify(address))
    } else {
      localStorage.removeItem(STORAGE_KEYS.WORK_ADDRESS)
    }
  } catch (error) {
    console.error('Error saving work address:', error)
  }
}

// Recent addresses
const MAX_RECENT_ADDRESSES = 10

export const getRecentAddresses = (): AddressResult[] => {
  try {
    const recent = localStorage.getItem(STORAGE_KEYS.RECENT_ADDRESSES)
    return recent ? JSON.parse(recent) : []
  } catch (error) {
    console.error('Error reading recent addresses:', error)
    return []
  }
}

export const addRecentAddress = (address: AddressResult): void => {
  try {
    const recent = getRecentAddresses()
    
    // Remove duplicate if exists
    const filtered = recent.filter(
      (a) => a.lat !== address.lat || a.lon !== address.lon
    )
    
    // Add to beginning and limit size
    const updated = [address, ...filtered].slice(0, MAX_RECENT_ADDRESSES)
    
    localStorage.setItem(STORAGE_KEYS.RECENT_ADDRESSES, JSON.stringify(updated))
  } catch (error) {
    console.error('Error saving recent address:', error)
  }
}

export const clearRecentAddresses = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.RECENT_ADDRESSES)
  } catch (error) {
    console.error('Error clearing recent addresses:', error)
  }
}

// User preferences
export interface UserPreferences {
  preferredTariff?: string
  preferUSDT?: boolean
  lastUsedRequirements?: {
    childchair?: boolean
    nosmoking?: boolean
    conditioner?: boolean
  }
}

export const getUserPreferences = (): UserPreferences => {
  try {
    const prefs = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES)
    return prefs ? JSON.parse(prefs) : {}
  } catch (error) {
    console.error('Error reading user preferences:', error)
    return {}
  }
}

export const saveUserPreferences = (prefs: Partial<UserPreferences>): void => {
  try {
    const current = getUserPreferences()
    const updated = { ...current, ...prefs }
    localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(updated))
  } catch (error) {
    console.error('Error saving user preferences:', error)
  }
}
